<template>
	<div class="asset-details-about-company__secondary-item">
		<component :is="mediaComponent.component" v-bind="mediaComponent.props" @click="handleClick()">
			<span class="asset-details-about-company__secondary-item-content"
				><img :src="icon.url" :alt="icon.altText" role="presentation" style="height: 16px; width: 16px" /><span
					class="body-sm text-align-left"
					>{{ props.item.label }}</span
				></span
			>
		</component>
	</div>
</template>

<script setup lang="ts">
import { AssetSecondaryMediaItem } from 'types/assets';
import BaseButton from '@components/_base-button.vue';
import BaseLink from '@components/_base-link.vue';
import { computed } from 'vue';
import { DynamicComponent } from 'types/vue';
import { InvestorUpdateMediaItem } from 'types/account';
import { Link } from 'types/layout';
import MediaGalleryModal from '@components/account/media-gallery-modal.vue';
import { useAppStore } from '@stores/app';

interface Props {
	item: AssetSecondaryMediaItem;
}

const props = defineProps<Props>();

const appStore = useAppStore();

const icon = computed((): { url: string; altText: string } => ({
	url: props.item.icon?.url,
	altText: 'Icon for media'
}));

const webLink = computed((): Link | null =>
	props.item.mediaExternalURL?.url
		? {
				href: props.item.mediaExternalURL.url,
				target: props.item.mediaExternalURL.openInNewTab ? '_blank' : ''
			}
		: null
);

const mediaComponent = computed(
	(): DynamicComponent =>
		webLink.value
			? {
					component: BaseLink,
					props: { link: webLink.value, classes: 'text-color-current-color width-100' }
				}
			: {
					component: BaseButton,
					props: { classes: 'width-100' }
				}
);

const mediaGallery = computed((): Array<InvestorUpdateMediaItem> | null => {
	const { mediaItemImage, mediaItemVideo } = props.item;

	if (!mediaItemImage && !mediaItemVideo) {
		return null;
	}

	const mediaUrl = mediaItemImage?.url || mediaItemVideo?.url || '';
	const mediaItemType = mediaItemImage ? 'PHOTO' : 'VIDEO';

	return [
		{
			itemOrder: '0',
			thumbnailUrl: '',
			sourceUrl: mediaUrl,
			mediaItemType
		}
	];
});

function handleClick(): void {
	if (!!props.item.mediaItemImage || !!props.item.mediaItemVideo) {
		appStore.updateCurrentModal({
			modal: MediaGalleryModal,
			props: {
				mediaItems: mediaGallery.value,
				isYoutubeVideo: false
			}
		});
	}
}
</script>

<style lang="scss">
@use '../../../styles/constants/colors.scss' as *;
@use '../../../styles/utilities/respond-to.scss' as *;

.asset-details-about-company {
	&__secondary-item {
		display: flex;
		align-items: center;
		border: 1px solid token('bg-tertiary');
		border-radius: 8px;
		cursor: pointer;

		@include respond-to(lg) {
			width: 50%;
		}

		&-content {
			display: flex;
			gap: 16px;
			align-items: center;
			padding: 8px;
		}

		&:hover {
			background-color: token('bg-secondary');
			border: 1px solid token('content-primary');

			> a {
				color: currentColor;
			}
		}
	}
}
</style>
